import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Search,
    Mic,
    RefreshCw,
    FileText,
    Eye,
    Printer,
    Pencil,
    Trash2,
    Package,
    Weight,
    Truck,
    CalendarDays,
    X,
    Download
} from "lucide-react";

import * as XLSX from "xlsx";

import {
    getDeliveries,
    deleteDelivery
} from "../services/api";

import { useNavigate } from "react-router-dom";

import "./DeliveryChallans.css";


/* =========================================================
   DELIVERY CHALLANS PAGE
========================================================= */

const DeliveryChallans = () => {

    const navigate = useNavigate();


    /* =====================================================
       STATE
    ===================================================== */

    const [deliveries, setDeliveries] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [registerListening, setRegisterListening] = useState(false);

    const startRegisterVoiceSearch = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            window.alert("Voice search requires an updated Chrome browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "mr-IN";
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            const spokenText = event.results?.[0]?.[0]?.transcript || "";
            if (spokenText.trim()) setSearch(spokenText.trim());
        };
        recognition.onerror = (event) => {
            if (event.error === "not-allowed") {
                window.alert("Please allow microphone access in your browser.");
            } else if (event.error === "no-speech") {
                window.alert("No speech detected. Please try again.");
            }
        };
        recognition.onend = () => setRegisterListening(false);

        try {
            setRegisterListening(true);
            recognition.start();
        } catch (error) {
            setRegisterListening(false);
            console.error("Voice search failed:", error);
        }
    };


    const [selectedChallan, setSelectedChallan] =
        useState(null);

    const [deleting, setDeleting] =
        useState(false);

    const [exporting, setExporting] =
        useState(false);


    /* =====================================================
       LOAD DELIVERY CHALLANS
    ===================================================== */

    const loadDeliveries = async () => {

        try {

            setLoading(true);

            setError("");

            const response =
                await getDeliveries();

            const data =
                response?.data?.data ||
                response?.data ||
                [];

            setDeliveries(
                Array.isArray(data)
                    ? data
                    : []
            );

        } catch (err) {

            console.error(
                "Delivery Challans Load Error:",
                err
            );

            setError(
                err?.response?.data?.message ||
                "Failed to load delivery challans."
            );

        } finally {

            setLoading(false);

        }

    };


    /* =====================================================
       INITIAL LOAD
    ===================================================== */

    useEffect(() => {

        loadDeliveries();

    }, []);


    /* =====================================================
       FORMAT DATE
    ===================================================== */

    const formatDate = (date) => {

        if (!date) {
            return "-";
        }

        const d =
            new Date(date);

        if (
            Number.isNaN(
                d.getTime()
            )
        ) {

            return date;

        }

        return d.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
            }
        );

    };


    /* =====================================================
       FORMAT NUMBER
    ===================================================== */

    const formatNumber = (value) => {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN"
        );

    };


    /* =====================================================
       FILTER DATA
    ===================================================== */

    const filteredDeliveries =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();

            if (!keyword) {
                return deliveries;
            }

            return deliveries.filter(
                (item) => {

                    return (

                        String(
                            item.challan_no || ""
                        )
                            .toLowerCase()
                            .includes(keyword)

                        ||

                        String(
                            item.customer_name || ""
                        )
                            .toLowerCase()
                            .includes(keyword)

                        ||

                        String(
                            item.vehicle_no || ""
                        )
                            .toLowerCase()
                            .includes(keyword)

                        ||

                        String(
                            item.total_bags || ""
                        )
                            .toLowerCase()
                            .includes(keyword)

                        ||

                        String(
                            item.party_no || ""
                        )
                            .toLowerCase()
                            .includes(keyword)

                    );

                }
            );

        }, [
            deliveries,
            search
        ]);


    /* =====================================================
       SUMMARY VALUES
    ===================================================== */

    const totalChallans =
        deliveries.length;


    const totalBags =
        deliveries.reduce(
            (sum, item) =>
                sum +
                Number(
                    item.total_bags || 0
                ),
            0
        );


    const totalWeight =
        deliveries.reduce(
            (sum, item) =>
                sum +
                Number(
                    item.total_weight_kg || 0
                ),
            0
        );


    const totalWeightTons =
        totalWeight / 1000;


    /* =====================================================
       EXPORT TO EXCEL
    ===================================================== */

    const handleExportExcel = () => {

        try {

            if (
                filteredDeliveries.length === 0
            ) {

                alert(
                    "There are no delivery challans available to export."
                );

                return;

            }

            setExporting(true);


            /* =========================================
               PREPARE EXCEL DATA
            ========================================= */

            const excelData =
                filteredDeliveries.map(
                    (item, index) => {

                        const totalWeightKg =
                            Number(
                                item.total_weight_kg ||
                                0
                            );

                        const netWeightKg =
                            Number(
                                item.net_weight_kg ||
                                0
                            );

                        return {

                            "Sr. No.":
                                index + 1,

                            "Challan No.":
                                item.challan_no ||
                                "-",

                            "Challan Date":
                                formatDate(
                                    item.challan_date
                                ),

                            "Party No.":
                                item.party_no ||
                                "-",

                            "Customer Name":
                                item.customer_name ||
                                "-",

                            "Vehicle No.":
                                item.vehicle_no ||
                                "-",

                            "Driver Name":
                                item.driver_name ||
                                "-",

                            "Total Bags":
                                Number(
                                    item.total_bags ||
                                    0
                                ),

                            "Weight (KG)":
                                totalWeightKg,

                            "Weight (Tons)":
                                Number(
                                    (
                                        totalWeightKg /
                                        1000
                                    ).toFixed(2)
                                ),

                            "Full Lorry Weight (KG)":
                                Number(
                                    item.full_lorry_weight_kg ||
                                    0
                                ),

                            "Empty Lorry Weight (KG)":
                                Number(
                                    item.empty_lorry_weight_kg ||
                                    0
                                ),

                            "Lorry Net Weight (KG)":
                                netWeightKg,

                            "Lorry Net Weight (Tons)":
                                Number(
                                    (
                                        netWeightKg /
                                        1000
                                    ).toFixed(2)
                                ),

                            "Remarks":
                                item.remarks ||
                                ""

                        };

                    }
                );


            /* =========================================
               CREATE WORKSHEET
            ========================================= */

            const worksheet =
                XLSX.utils.json_to_sheet(
                    excelData
                );


            /* =========================================
               COLUMN WIDTHS
            ========================================= */

            worksheet["!cols"] = [

                {
                    wch: 10
                },

                {
                    wch: 18
                },

                {
                    wch: 15
                },

                {
                    wch: 15
                },

                {
                    wch: 30
                },

                {
                    wch: 18
                },

                {
                    wch: 22
                },

                {
                    wch: 14
                },

                {
                    wch: 16
                },

                {
                    wch: 16
                },

                {
                    wch: 24
                },

                {
                    wch: 25
                },

                {
                    wch: 25
                },

                {
                    wch: 25
                },

                {
                    wch: 30
                }

            ];


            /* =========================================
               CREATE WORKBOOK
            ========================================= */

            const workbook =
                XLSX.utils.book_new();


            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Delivery Challans"
            );


            /* =========================================
               FILE NAME
            ========================================= */

            const today =
                new Date()
                    .toISOString()
                    .split("T")[0];

            const fileName =
                `Delivery_Challans_${today}.xlsx`;


            /* =========================================
               DOWNLOAD EXCEL
            ========================================= */

            XLSX.writeFile(
                workbook,
                fileName
            );


        } catch (err) {

            console.error(
                "Excel Export Error:",
                err
            );

            alert(
                "Unable to export delivery challans to Excel."
            );

        } finally {

            setExporting(false);

        }

    };


    /* =====================================================
       VIEW CHALLAN
    ===================================================== */

    const handleView = (id) => {

        navigate(
            `/delivery-challans/print/${id}`
        );

    };


    /* =====================================================
       EDIT CHALLAN
    ===================================================== */

    const handleEdit = (id) => {

        navigate(
            `/delivery?edit=${id}`
        );

    };


    /* =====================================================
       PRINT CHALLAN
    ===================================================== */

    const handlePrint = (id) => {

        navigate(
            `/delivery-challans/print/${id}`
        );

    };


    /* =====================================================
       DELETE CONFIRMATION
    ===================================================== */

    const handleDeleteClick = (
        challan
    ) => {

        setSelectedChallan(
            challan
        );

    };


    /* =====================================================
       DELETE CHALLAN
    ===================================================== */

    const confirmDelete =
        async () => {

            if (
                !selectedChallan?.id
            ) {

                return;

            }

            try {

                setDeleting(true);

                await deleteDelivery(
                    selectedChallan.id
                );

                setSelectedChallan(
                    null
                );

                await loadDeliveries();

            } catch (err) {

                console.error(
                    "Delete Delivery Error:",
                    err
                );

                alert(
                    err?.response?.data?.message ||
                    "Failed to delete delivery challan."
                );

            } finally {

                setDeleting(false);

            }

        };


    /* =====================================================
       LOADING
    ===================================================== */

    if (loading) {

        return (

            <div className="delivery-challans-page">

                <div className="delivery-loading">

                    <RefreshCw
                        size={26}
                        className="delivery-spin"
                    />

                    <span>
                        Loading delivery challans...
                    </span>

                </div>

            </div>

        );

    }


    /* =====================================================
       PAGE
    ===================================================== */

    return (

        <div className="delivery-challans-page">


            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <div className="delivery-page-header">

                <div>

                    <div className="delivery-title-row">

                        <div className="delivery-title-icon">

                            <FileText
                                size={25}
                            />

                        </div>

                        <div>

                            <h1>
                                Delivery Challans
                            </h1>

                            <p>
                                Manage all outgoing goods
                                and delivery transactions
                            </p>

                        </div>

                    </div>

                </div>


                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                        flexWrap: "wrap"
                    }}
                >

                    {/* =====================================
                        EXPORT EXCEL
                    ===================================== */}

                    <button
                        type="button"
                        className="delivery-refresh-btn"
                        onClick={
                            handleExportExcel
                        }
                        disabled={
                            exporting ||
                            filteredDeliveries.length === 0
                        }
                        style={{
                            background:
                                exporting
                                    ? "#9ca3af"
                                    : "#217346",
                            borderColor:
                                exporting
                                    ? "#9ca3af"
                                    : "#217346"
                        }}
                    >

                        {exporting ? (

                            <RefreshCw
                                size={18}
                                className="delivery-spin"
                            />

                        ) : (

                            <Download
                                size={18}
                            />

                        )}

                        {exporting
                            ? "Exporting..."
                            : "Export Excel"
                        }

                    </button>


                    {/* =====================================
                        REFRESH
                    ===================================== */}

                    <button
                        type="button"
                        className="delivery-refresh-btn"
                        onClick={
                            loadDeliveries
                        }
                    >

                        <RefreshCw
                            size={18}
                        />

                        Refresh

                    </button>

                </div>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="delivery-error">

                    <strong>
                        Error:
                    </strong>

                    <span>
                        {error}
                    </span>

                </div>

            )}


            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            <div className="delivery-summary-grid">


                {/* TOTAL CHALLANS */}

                <div className="delivery-summary-card">

                    <div className="delivery-summary-icon blue">

                        <FileText
                            size={23}
                        />

                    </div>

                    <div>

                        <span>
                            Total Challans
                        </span>

                        <strong>
                            {formatNumber(
                                totalChallans
                            )}
                        </strong>

                    </div>

                </div>


                {/* TOTAL BAGS */}

                <div className="delivery-summary-card">

                    <div className="delivery-summary-icon purple">

                        <Package
                            size={23}
                        />

                    </div>

                    <div>

                        <span>
                            Total Bags Delivered
                        </span>

                        <strong>
                            {formatNumber(
                                totalBags
                            )}
                        </strong>

                    </div>

                </div>


                {/* TOTAL WEIGHT */}

                <div className="delivery-summary-card">

                    <div className="delivery-summary-icon green">

                        <Weight
                            size={23}
                        />

                    </div>

                    <div>

                        <span>
                            Total Weight
                        </span>

                        <strong>
                            {totalWeightTons.toLocaleString(
                                "en-IN",
                                {
                                    maximumFractionDigits: 2
                                }
                            )} Tons
                        </strong>

                    </div>

                </div>


                {/* TRANSACTIONS */}

                <div className="delivery-summary-card">

                    <div className="delivery-summary-icon orange">

                        <Truck
                            size={23}
                        />

                    </div>

                    <div>

                        <span>
                            Delivery Transactions
                        </span>

                        <strong>
                            {formatNumber(
                                filteredDeliveries.length
                            )}
                        </strong>

                    </div>

                </div>

            </div>


            {/* =================================================
                SEARCH / FILTER
            ================================================= */}

            <div className="delivery-filter-panel">

                <div className="delivery-search-box">

                    <Search
                        size={20}
                    />

                    <input
                        type="text"
                        placeholder="Search challan no., customer, vehicle, party no. or bags..."
                        value={search}
                        onChange={(e) =>
                            setSearch(
                                e.target.value
                            )
                        }
                    />

                    <button
                        type="button"
                        className={`register-voice-button ${registerListening ? "is-listening" : ""}`}
                        onClick={startRegisterVoiceSearch}
                        title="Marathi voice search"
                        aria-label="Marathi voice search"
                    >
                        <Mic size={18} />
                    </button>

                    {search && (

                        <button
                            type="button"
                            className="delivery-clear-search"
                            onClick={() =>
                                setSearch("")
                            }
                        >

                            <X
                                size={18}
                            />

                        </button>

                    )}

                </div>


                <div className="delivery-filter-info">

                    <span>
                        Showing
                    </span>

                    <strong>
                        {filteredDeliveries.length}
                    </strong>

                    <span>
                        of
                    </span>

                    <strong>
                        {deliveries.length}
                    </strong>

                    <span>
                        challans
                    </span>

                </div>

            </div>


            {/* =================================================
                TABLE PANEL
            ================================================= */}

            <div className="delivery-table-panel">

                <div className="delivery-table-header">

                    <div>

                        <h2>
                            Delivery Transactions
                        </h2>

                        <p>
                            Complete record of outgoing
                            goods from cold storage
                        </p>

                    </div>

                </div>


                <div className="delivery-table-wrapper">

                    <table className="delivery-table">

                        <thead>

                            <tr>

                                <th>
                                    Challan No.
                                </th>

                                <th>

                                    <span className="th-with-icon">

                                        <CalendarDays
                                            size={16}
                                        />

                                        Date

                                    </span>

                                </th>

                                <th>
                                    Customer
                                </th>

                                <th>
                                    Vehicle
                                </th>

                                <th>
                                    Bags
                                </th>

                                <th>
                                    Weight
                                </th>

                                <th>
                                    Lorry Net
                                </th>

                                <th>
                                    Actions
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {filteredDeliveries.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="8"
                                        className="delivery-empty"
                                    >

                                        <div>

                                            <FileText
                                                size={42}
                                            />

                                            <h3>
                                                No delivery challans found
                                            </h3>

                                            <p>

                                                {search
                                                    ? "Try changing your search."
                                                    : "No delivery challans have been created yet."
                                                }

                                            </p>

                                        </div>

                                    </td>

                                </tr>

                            ) : (

                                filteredDeliveries.map(
                                    (item) => (

                                        <tr
                                            key={
                                                item.id
                                            }
                                        >


                                            {/* CHALLAN NUMBER */}

                                            <td>

                                                <div className="challan-number">

                                                    <div className="challan-small-icon">

                                                        <FileText
                                                            size={16}
                                                        />

                                                    </div>

                                                    <strong>
                                                        {
                                                            item.challan_no
                                                        }
                                                    </strong>

                                                </div>

                                            </td>


                                            {/* DATE */}

                                            <td>

                                                <div className="date-cell">

                                                    <CalendarDays
                                                        size={16}
                                                    />

                                                    {
                                                        formatDate(
                                                            item.challan_date
                                                        )
                                                    }

                                                </div>

                                            </td>


                                            {/* CUSTOMER */}

                                            <td>

                                                <div className="customer-cell">

                                                    <strong>

                                                        {
                                                            item.customer_name ||
                                                            "-"
                                                        }

                                                    </strong>

                                                </div>

                                            </td>


                                            {/* VEHICLE */}

                                            <td>

                                                <div className="vehicle-cell">

                                                    <Truck
                                                        size={17}
                                                    />

                                                    <span>

                                                        {
                                                            item.vehicle_no ||
                                                            "-"
                                                        }

                                                    </span>

                                                </div>

                                            </td>


                                            {/* BAGS */}

                                            <td>

                                                <strong className="bags-value">

                                                    {formatNumber(
                                                        item.total_bags
                                                    )}

                                                </strong>

                                                <span className="unit-label">
                                                    Bags
                                                </span>

                                            </td>


                                            {/* WEIGHT */}

                                            <td>

                                                <strong className="weight-value">

                                                    {(
                                                        Number(
                                                            item.total_weight_kg ||
                                                            0
                                                        ) / 1000
                                                    ).toLocaleString(
                                                        "en-IN",
                                                        {
                                                            maximumFractionDigits: 2
                                                        }
                                                    )}

                                                </strong>

                                                <span className="unit-label">
                                                    Tons
                                                </span>

                                            </td>


                                            {/* LORRY NET */}

                                            <td>

                                                <strong className="lorry-weight">

                                                    {(
                                                        Number(
                                                            item.net_weight_kg ||
                                                            0
                                                        ) / 1000
                                                    ).toLocaleString(
                                                        "en-IN",
                                                        {
                                                            maximumFractionDigits: 2
                                                        }
                                                    )}

                                                </strong>

                                                <span className="unit-label">
                                                    Tons
                                                </span>

                                            </td>


                                            {/* ACTIONS */}

                                            <td>

                                                <div className="delivery-actions">


                                                    {/* VIEW */}

                                                    <button
                                                        type="button"
                                                        className="action-btn view"
                                                        title="View Challan"
                                                        onClick={() =>
                                                            handleView(
                                                                item.id
                                                            )
                                                        }
                                                    >

                                                        <Eye
                                                            size={17}
                                                        />

                                                    </button>


                                                    {/* PRINT */}

                                                    <button
                                                        type="button"
                                                        className="action-btn print"
                                                        title="Print Challan"
                                                        onClick={() =>
                                                            handlePrint(
                                                                item.id
                                                            )
                                                        }
                                                    >

                                                        <Printer
                                                            size={17}
                                                        />

                                                    </button>


                                                    {/* EDIT */}

                                                    <button
                                                        type="button"
                                                        className="action-btn edit"
                                                        title="Edit Challan"
                                                        onClick={() =>
                                                            handleEdit(
                                                                item.id
                                                            )
                                                        }
                                                    >

                                                        <Pencil
                                                            size={17}
                                                        />

                                                    </button>


                                                    {/* DELETE */}

                                                    <button
                                                        type="button"
                                                        className="action-btn delete"
                                                        title="Delete Challan"
                                                        onClick={() =>
                                                            handleDeleteClick(
                                                                item
                                                            )
                                                        }
                                                    >

                                                        <Trash2
                                                            size={17}
                                                        />

                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    )
                                )

                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* =================================================
                DELETE CONFIRMATION MODAL
            ================================================= */}

            {selectedChallan && (

                <div className="delivery-modal-overlay">

                    <div className="delivery-delete-modal">


                        <div className="delete-modal-icon">

                            <Trash2
                                size={27}
                            />

                        </div>


                        <h2>
                            Delete Delivery Challan?
                        </h2>


                        <p>

                            Are you sure you want to delete

                            <strong>
                                {" "}
                                {
                                    selectedChallan.challan_no
                                }
                            </strong>

                            ?

                        </p>


                        <span className="delete-warning">

                            The delivered stock will be returned
                            to inventory.

                        </span>


                        <div className="delete-modal-actions">


                            <button
                                type="button"
                                className="delete-cancel-btn"
                                onClick={() =>
                                    setSelectedChallan(
                                        null
                                    )
                                }
                                disabled={
                                    deleting
                                }
                            >

                                Cancel

                            </button>


                            <button
                                type="button"
                                className="delete-confirm-btn"
                                onClick={
                                    confirmDelete
                                }
                                disabled={
                                    deleting
                                }
                            >

                                {deleting ? (

                                    <>

                                        <RefreshCw
                                            size={17}
                                            className="delivery-spin"
                                        />

                                        Deleting...

                                    </>

                                ) : (

                                    <>

                                        <Trash2
                                            size={17}
                                        />

                                        Delete Challan

                                    </>

                                )}

                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>

    );

};


export default DeliveryChallans;